import API from "serviceshift-ui/api-client";
import { authenticityToken } from "@/lib/helpers";

export default {
  // `ignoreError` lets a page show the server's 403/422 message inline instead of the global toast.
  createBatch(_, { link, data, ignoreError }) {
    return API.makeRequest(link, {
      method: "POST",
      data,
      ...(ignoreError && { headers: { ignoreError: true } }),
    });
  },
  fetchImportVendors({ commit }, link) {
    return API.makeRequest(link, {
      method: "GET",
    }).then((res) => {
      commit("replace", {
        state: "importVendors",
        data: res.data,
      });
    });
  },
  getS3UploadURL(_, { link, filename, ignoreError }) {
    return API.makeRequest(link, {
      method: "POST",
      data: { filename },
      ...(ignoreError && { headers: { ignoreError: true } }),
    });
  },
  s3Upload(_, { data, file, body }) {
    const formData = new FormData();
    Object.keys(data.parameters).forEach((key) => {
      formData.append(key, data.parameters[key]);
    });
    formData.append("file", file);
    return API.makeRequest(data.server_url, {
      method: "POST",
      data: formData,
      ...body,
    }).then(() => {
      return data.parameters.key;
    });
  },
  fetchImportBatches(_, fetch_batch_url) {
    return API.makeRequest(fetch_batch_url, {
      method: "GET",
    });
  },
  // The mapping screen shows its own 409/422 messages, so these skip the global error toast.
  fetchColumnMapping(_, url) {
    return API.makeRequest(url, {
      method: "GET",
      headers: { ignoreError: true }
    });
  },
  // `sample` (0-based saved row) picks which row the preview is built from; omitted = first values.
  saveColumnMapping(_, { url, fields, sample }) {
    return API.makeRequest(url, {
      method: "PUT",
      data: sample == null ? { fields } : { fields, sample },
      headers: { ignoreError: true }
    });
  },
  startColumnMappedLoad(_, url) {
    return API.makeRequest(url, {
      method: "POST",
      headers: { ignoreError: true }
    });
  },
  getColumnMappingSample(_, { url, sample }) {
    return API.makeRequest(url, {
      method: "GET",
      params: sample == null ? undefined : { sample },
      headers: { ignoreError: true }
    });
  },
  resetColumnMapping(_, url) {
    return API.makeRequest(url, {
      method: "POST",
      headers: { ignoreError: true }
    });
  },
  // The import pages (upload, map, review) show their own 403/404/409/422 messages.
  fetchImportsPageData() {
    return API.makeRequest("/imports/initial_page_data", {
      method: "GET",
      headers: { ignoreError: true }
    });
  },
  fetchDataLoadSources(_, url) {
    return API.makeRequest(url, {
      method: "GET",
      headers: { ignoreError: true }
    });
  },
  fetchImport(_, { batchType, id }) {
    return API.makeRequest(`/imports/${batchType}/${id}.json`, {
      method: "GET",
      headers: { ignoreError: true }
    });
  },
  // Non-API controller: needs the CSRF token. Failures use the global toast (the server sends `message`).
  wipeImport(_, url) {
    return API.makeRequest(url, {
      method: "POST",
      headers: { "X-CSRF-Token": authenticityToken() }
    });
  },
  // Non-API controller: needs the CSRF token. The modal shows the server's 409/422 message itself.
  replaceImportFile(_, { url, data }) {
    return API.makeRequest(url, {
      method: "POST",
      data,
      headers: { "X-CSRF-Token": authenticityToken(), ignoreError: true }
    });
  },
  performBatchImport(_, url) {
    return API.makeRequest(url, {
      method: "POST",
    });
  },
  cancelBatchRun(_, url) {
    return API.makeRequest(url, {
      method: "POST"
    });
  },
  performBatchDryRun(_, url) {
    return API.makeRequest(url, {
      method: "POST",
      data: { dry_run: true }
    });
  },
  downloadBatchErrors(_, url) {
    // Every caller shows the server's message itself, so the interceptor must not toast it too.
    return API.makeRequest(url, {
      method: "GET",
      headers: { ignoreError: true }
    }).then((res) => {
      // A report that is still being prepared answers the probe with a message and no file.
      if (res.data && res.data.message) {
        return { ready: false, message: res.data.message };
      }

      if (url.includes('?')) url += '&download=true';
      else url += '?download=true';
      window.location.href = url;
      return { ready: true };
    });
  },
  downloadDryRunReport(_, url) {
    // The validation report is generated on demand, so this readiness probe can
    // answer "still preparing" (a non-2xx) — which is not a failure worth the
    // global interceptor's red toast. `ignoreError` silences it so the caller can
    // message the outcome; a clean run answers 200 with a message and no file.
    return API.makeRequest(url, {
      method: "GET",
      headers: { ignoreError: true }
    }).then((res) => {
      if (res.data && res.data.message) {
        return { ready: false, message: res.data.message };
      }

      const separator = url.includes("?") ? "&" : "?";
      window.location.href = `${url}${separator}download=true`;
      return { ready: true };
    });
  }
};
